/**
 * spaceStep.ts
 * Fixed-step entry point for the World 2 space shoot-em-up scene.
 *
 * Registered with the engine via `setActiveStep(stepSpace)` when
 * `SpaceScene.init()` runs. While registered, `runPhysicsFrame()` calls
 * `stepSpace(FIXED_STEP)` each fixed tick instead of the platformer step.
 *
 * Pipeline per tick:
 *  1. Auto-scroll camera right at `SPACE_AUTO_SCROLL_SPEED`.
 *  2. Step ship physics (input → velocity → position → viewport clamp).
 *  3. Decrement ship-attack cooldown.
 *  4. Try to fire a projectile (gated by attackHeld + cooldown).
 *  5. Update active projectiles (movement + culling).
 *  6. Roll asteroid spawner + update active asteroids.
 *  7. Resolve projectile↔asteroid collisions.
 *  8. Roll enemy spawner + update active enemies.
 *  9. Resolve projectile↔enemy collisions.
 *  9b. Trigger boss (if elapsed clock crosses threshold) + update boss +
 *      resolve projectile↔boss collisions.
 *  9c. Roll homing-mine spawner (Level 2-3 only) + update active mines.
 *      (Steps 6–9c are delegated to `stepSpaceHazards` in
 *      `spaceHazardStep.ts` to keep this file under the 300-line cap.)
 * 10. Clear rising-edge input flags.
 *
 * NO React imports, NO Reanimated imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module spaceStep
 */

import { spaceState } from './spaceState';
import { stepShipPhysics } from './shipPhysics';
import {
  updateShipCooldown,
  tryFireShipProjectile,
  updateShipProjectiles,
  updateMissileCooldown,
  updateAttackHold,
  tryFireShipMissile,
  updateShipMissiles,
} from './shipCombat';
import { tickCameraShake } from './cameraShake';
import { stepSpaceHazards } from './spaceHazardStep';
import {
  updateSpaceShards,
  resolveShipShardCollisions,
} from './spaceShardSystem';
import {
  updateSpaceLoot,
  resolveShipLootCollisions,
} from './spaceLootSystem';
import { stepExplosions } from './explosionVfx';
import { tickShipIframes, resolveShipHazardCollisions } from './shipDamage';
import { updateSpaceHudSnapshot } from './spaceHudSnapshot';
import { clearRisingEdgeFlags } from '../input/inputState';
import {
  SPACE_AUTO_SCROLL_SPEED,
  SPACE_LEVEL_OUTRO_GRACE_S,
} from '../../constants/spaceConstants';

/**
 * Execute one fixed-step update for the active space scene.
 * No-op when the scene is paused (`isRunning === false`) or no ship exists.
 *
 * @param dt - Fixed timestep in seconds (always FIXED_STEP).
 */
export function stepSpace(dt: number): void {
  if (!spaceState.isRunning || spaceState.ship === null) return;

  // ── 1. Auto-scroll camera ──────────────────────────────────────────────────
  // DECISION: Camera advances BEFORE ship physics so the viewport-clamp inside
  // stepShipPhysics uses the just-updated cameraX. The alternative — clamp
  // first, then scroll — would let the ship drift one tick behind the camera's
  // left edge, accumulating a 1-frame "slip" that becomes visible at higher
  // scroll speeds.
  spaceState.cameraX += SPACE_AUTO_SCROLL_SPEED * dt;
  spaceState.elapsedSeconds += dt;

  // DECISION: Compute a `spawnGate` BEFORE running the spawners so the tick
  // that crosses a duration-based end threshold is itself gated. The
  // end-condition watcher at the bottom of this function only flips
  // `levelComplete` AFTER spawners run, so without this pre-tick check the
  // crossing tick would push one final asteroid/enemy/boss into the viewport
  // behind the Sector Cleared overlay. Boss-defeated completion is not
  // pre-computed here because the boss reaches `DEFEATED` inside `updateBoss`
  // (called below); for that path the next-tick gate is correct.
  const cfg = spaceState.levelConfig;
  const durationWillCompleteThisTick =
    cfg.endCondition === 'duration' &&
    cfg.durationSeconds !== null &&
    spaceState.elapsedSeconds >= cfg.durationSeconds;
  // DECISION (W2 overhaul): The spawn gate now also cuts off while
  // the outro grace is ticking down. Once the end-condition fires,
  // no fresh asteroid/enemy/boss should appear during the 1.5 s
  // settle window — only the entities that are already on screen
  // keep integrating so they can drift off naturally.
  const spawnGate =
    !spaceState.levelComplete &&
    !durationWillCompleteThisTick &&
    spaceState.outroTimer === null;

  // ── 2. Ship physics ───────────────────────────────────────────────────────
  stepShipPhysics(spaceState.ship, dt, spaceState.cameraX);

  // ── 3–5. Ship combat ───────────────────────────────────────────────────────
  // Order: decay cooldown → try to fire (may set cooldown) → update existing
  // projectiles. Firing before updating means a brand-new projectile travels
  // for `dt` seconds on the same tick it is spawned, matching the platformer
  // Star Shard pipeline and avoiding a visible one-frame stall at the muzzle.
  updateShipCooldown(dt);
  tryFireShipProjectile();
  updateShipProjectiles(dt);

  // ── 5b. Ship secondary weapon (missile) ───────────────────────────────────
  // Same shape as the projectile pipeline. Cross-pool collision runs
  // inside `stepSpaceHazards` (section 9b·5) so AoE fans out across every
  // live hazard pool from one impact point.
  updateMissileCooldown(dt);
  updateAttackHold(dt);
  tryFireShipMissile();
  updateShipMissiles(dt);

  // ── 6–9c. Hazard pools ─────────────────────────────────────────────────────
  // Asteroids, enemies (Poisson + wave-script), boss + boss weapons, the
  // ship-missile cross-pool splash, and the Level 2-3 homing mines — one
  // cohesive "spawn → integrate → resolve collisions" phase extracted to
  // `spaceHazardStep.ts` to keep this entry point under the 300-line cap.
  // Positioned exactly where the inline block used to run so the ordering
  // guarantees (asteroids before enemies before boss before mines) hold.
  stepSpaceHazards(dt, spawnGate);

  // ── 9c·5. Constellation shards ─────────────────────────────────────────────
  // Drift + cull pass runs unconditionally so a level-config swap can't
  // leak prior shards in. Ship-vs-shard pickup collision runs after
  // every other hazard pass so a ship that just took fatal damage this
  // tick (gameOver flipped) cannot also bank a shard — the resolver
  // skips when gameOver is true. No spawner: shards are level-placed
  // by `SpaceScene.init()` via `spawnShardsFromLevel(config)`.
  updateSpaceShards(dt);
  resolveShipShardCollisions();

  // ── 9c·6. Star seed loot ─────────────────────────────────────
  // Drift + cull pass runs unconditionally; pickup-collision pass
  // runs immediately after so a kill that happened this tick can be
  // banked the same tick (loot drops at the kill site and the ship
  // overlap-check uses current-tick positions). Skipped via the
  // gameOver guard inside `resolveShipLootCollisions`.
  updateSpaceLoot(dt);
  resolveShipLootCollisions();

  // ── 9d. Ship damage ────────────────────────────────────────────────────────
  // Iframe clock decrements first so a hazard contact this tick that
  // happens to coincide with the EXACT expiry frame still registers as
  // "iframes just ended → take damage". Hazard resolution runs AFTER
  // every entity-update pass so positions are current-tick — a missile
  // that homed onto the ship this frame can't be missed.
  //
  // DECISION: Skipped entirely once `spaceState.gameOver` flips so the
  // couple of frames before the React layer freezes the canvas can't
  // keep applying damage to an already-dead ship.
  tickShipIframes(dt);
  if (!spaceState.gameOver) {
    resolveShipHazardCollisions();
  }

  // ── 9e. VFX ────────────────────────────────────────────────────────────────
  // Advance the pooled explosion bursts. Runs unconditionally so an in-flight
  // burst spawned the tick before `gameOver` flipped still finishes its
  // animation. The pool is asset-agnostic; the renderer reads frame indices
  // off the elapsed timer in the draw pass.
  stepExplosions(dt);

  // ── 9f. Camera shake ──────────────────────────────────────────────────────
  // Decays the active shake's magnitude linearly and writes the per-tick
  // (shakeX, shakeY) offsets the renderer reads. Runs after VFX so an
  // explosion spawned this tick can't fight the shake offset on its first
  // draw frame. Unconditional: a shake that started the tick before
  // gameOver should still play out so the death blow has weight.
  tickCameraShake(dt);

  // End-condition watcher. The condition (duration elapsed or boss
  // defeated) seeds an outro grace timer the first tick it fires;
  // `levelComplete` only flips once that timer counts down to <= 0
  // so the screen has a beat to settle before the Sector Cleared
  // overlay lands.
  if (!spaceState.levelComplete) {
    if (spaceState.outroTimer === null) {
      const conditionMet =
        durationWillCompleteThisTick ||
        (cfg.endCondition === 'boss-defeated' &&
          spaceState.boss !== null &&
          spaceState.boss.state === 'DEFEATED');
      if (conditionMet) {
        // DECISION (W2 overhaul): Seed with the constant grace value
        // — the same 1.5 s regardless of end-condition kind so the
        // outro pacing is consistent across duration / boss-defeated
        // levels.
        spaceState.outroTimer = SPACE_LEVEL_OUTRO_GRACE_S;
      }
    } else {
      spaceState.outroTimer -= dt;
      if (spaceState.outroTimer <= 0) {
        spaceState.outroTimer = null;
        spaceState.levelComplete = true;
      }
    }
  }

  // ── 10. Clear rising-edge inputs ───────────────────────────────────────────
  // Mirror of the platformer step so jumpPressed / attackPressed never persist
  // into the next tick. Held flags (left/right/up/down) are not cleared here.
  clearRisingEdgeFlags();

  // ── 11. HUD snapshot ──────────────────────────────────────────────────────
  // DECISION (PR #124 review): Write the read-only snapshot that
  // `src/game/ui/SpaceHUD.tsx` polls, so UI code never touches engine
  // state directly. Mirrors the `updateHudSnapshot` call in the
  // platformer `physicsStep`. `spaceState.ship` is guaranteed non-null
  // here because the early-out at the top of this function bails when
  // it is null.
  updateSpaceHudSnapshot(
    spaceState.ship.health,
    spaceState.ship.maxHealth,
    cfg.name,
  );
}
